import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import { updateUserProfile } from "../store/authSlice";

const pickProfile = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.profile && typeof data.profile === "object") return data.profile;
  if (data.user && typeof data.user === "object") return data.user;
  if (
    data.result &&
    typeof data.result === "object" &&
    !Array.isArray(data.result)
  ) {
    return data.result;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  if (data._id || data.id || data.userid || data.email || data.name)
    return data;
  return null;
};

const formatDateTime = (iso) => {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const formatDate = (iso) => {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-IN", { dateStyle: "medium" });
};

const formatMoney = (v) => {
  if (v == null || v === "") return null;
  const num = Number(v);
  if (Number.isNaN(num)) return null;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
};

const EXECUTIVE_STATUS_LABELS = {
  active: "Active",
  tenure_completion: "Tenure Completion",
  resigned: "Resigned",
  removed: "Removed",
};

const formatDMY = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
};

const DetailRow = ({ label, value }) => (
  <div className="row py-2 border-bottom" style={{ margin: 0 }}>
    <div
      className="col-12 col-sm-4 text-muted"
      style={{ fontSize: 13, fontWeight: 600 }}
    >
      {label}
    </div>
    <div className="col-12 col-sm-8" style={{ fontSize: 14 }}>
      {value != null && value !== "" ? String(value) : "—"}
    </div>
  </div>
);

const SectionTitle = ({ children, first }) => (
  <div
    className="text-muted text-uppercase"
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      marginTop: first ? 0 : 18,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const getProfileImageUrl = (p) =>
  String(p?.profileImage || p?.image || p?.avatar || "").trim();

const getProfilePhone = (p) =>
  String(p?.phone || p?.mobileNumber || p?.mobile || "").trim();

/** Normalize presign response shapes to { uploadUrl, key, contentType }. */
function unwrapPresignPayload(d) {
  if (!d || typeof d !== "object") return null;

  const nested =
    d.upload && typeof d.upload === "object"
      ? d.upload
      : d.data?.upload && typeof d.data.upload === "object"
        ? d.data.upload
        : null;

  if (nested?.uploadUrl && (nested?.key || nested?.s3Key)) {
    return {
      ...d,
      ...nested,
      key: nested.key || nested.s3Key,
    };
  }
  if (d.uploadUrl && (d.key || d.s3Key)) {
    return { ...d, key: d.key || d.s3Key };
  }
  if (d.data && typeof d.data === "object" && d.data.uploadUrl) {
    return {
      ...d,
      ...d.data,
      key: d.data.key || d.data.s3Key,
    };
  }
  return d;
}

function pickConfirmedImageUrl(data) {
  if (!data || typeof data !== "object") return "";
  return String(
    data.imageUrl ||
      data.image ||
      data.data?.imageUrl ||
      data.data?.image ||
      "",
  ).trim();
}

function pickConfirmedS3Key(data, fallback) {
  if (!data || typeof data !== "object") return fallback || "";
  return String(
    data.s3Key || data.key || data.data?.s3Key || fallback || "",
  ).trim();
}

const Profile = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const dispatch = useDispatch();
  const imageInputRef = useRef(null);
  const previewObjectUrlRef = useRef(null);

  const userId = useMemo(() => {
    try {
      return String(localStorage.getItem("userid") || "").trim();
    } catch {
      return "";
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  const [profile, setProfile] = useState(null);

  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editProfileImage, setEditProfileImage] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const clearLocalPreview = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearLocalPreview();
    },
    [clearLocalPreview],
  );

  const syncEditFromProfile = useCallback(
    (p) => {
      clearLocalPreview();
      const img = getProfileImageUrl(p);
      setEditName(String(p?.name || p?.fullName || "").trim());
      setEditPhone(getProfilePhone(p));
      setEditProfileImage(img);
      setImagePreview(img);
      if (imageInputRef.current) imageInputRef.current.value = "";
    },
    [clearLocalPreview],
  );

  const fetchProfile = useCallback(async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      setLoading(false);
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      setLoading(false);
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "getProfile",
        userId,
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        const p = pickProfile(data);
        if (p) {
          setProfile(p);
          if (!editingRef.current) syncEditFromProfile(p);
          const syncedName = String(p?.name || p?.fullName || "").trim();
          const syncedImage = getProfileImageUrl(p);
          dispatch(
            updateUserProfile({
              ...(syncedName ? { name: syncedName } : {}),
              profileImage: syncedImage,
            }),
          );
        } else {
          toast.error(data?.message || "Profile data not found in response");
          setProfile(null);
        }
      } else {
        toast.error(data?.message || "Failed to load profile");
        setProfile(null);
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Something went wrong",
      );
      setProfile(null);
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  }, [API_BASE_URL, userId, syncEditFromProfile, dispatch]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const startEdit = () => {
    syncEditFromProfile(profile);
    editingRef.current = true;
    setEditing(true);
  };

  const cancelEdit = () => {
    syncEditFromProfile(profile);
    editingRef.current = false;
    setEditing(false);
  };

  /**
   * Same flow as CommunityCreate:
   * 1) community.getProfileImageUploadUrl
   * 2) PUT binary to uploadUrl
   * 3) community.confirmProfileImage → public image URL
   */
  const uploadProfileImage = useCallback(
    async (email, file) => {
      if (!API_BASE_URL) {
        throw new Error("Missing REACT_APP_API_URL in environment");
      }
      const memberEmail = String(email || "").trim();
      if (!memberEmail) {
        throw new Error("Missing email. Please reload profile and try again.");
      }
      if (!file) {
        throw new Error("Please choose an image file.");
      }

      const fileName = file.name || "photo.jpg";
      const fileContentType =
        file.type && file.type.startsWith("image/") ? file.type : "image/jpeg";

      const presignRes = await axios.post(`${API_BASE_URL}`, {
        action: "community.getProfileImageUploadUrl",
        email: memberEmail,
        fileName,
        contentType: fileContentType,
      });
      const presignRaw = presignRes?.data;
      const okPresign =
        presignRaw?.status === "success" ||
        presignRaw?.success === true ||
        !!presignRaw?.uploadUrl ||
        !!presignRaw?.upload?.uploadUrl ||
        !!presignRaw?.data?.uploadUrl;

      if (!okPresign) {
        throw new Error(presignRaw?.message || "Could not get upload URL");
      }

      const presign = unwrapPresignPayload(presignRaw);
      const uploadUrl = presign?.uploadUrl;
      const s3KeyFromPresign = presign?.key || presign?.s3Key;
      if (!uploadUrl || !s3KeyFromPresign) {
        throw new Error("Invalid upload URL response (missing uploadUrl/key).");
      }

      const contentTypeForPut =
        presign.contentType || fileContentType || "image/jpeg";

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentTypeForPut },
        body: file,
      });

      if (!putRes.ok) {
        const errText = await putRes.text().catch(() => "");
        throw new Error(
          `S3 upload failed (${putRes.status}). ${
            errText
              ? errText.slice(0, 200)
              : "Check bucket CORS and Content-Type."
          }`,
        );
      }

      const confirmRes = await axios.post(`${API_BASE_URL}`, {
        action: "community.confirmProfileImage",
        email: memberEmail,
        s3Key: s3KeyFromPresign,
        fileName,
        contentType: contentTypeForPut,
      });
      const confirmData = confirmRes?.data;
      const okConfirm =
        confirmData?.status === "success" || confirmData?.success === true;

      if (!okConfirm) {
        throw new Error(
          confirmData?.message || "Could not confirm profile image",
        );
      }

      const imageUrl = pickConfirmedImageUrl(confirmData);
      if (!imageUrl) {
        throw new Error("Confirm succeeded but no image URL was returned");
      }

      return {
        imageUrl,
        s3Key: pickConfirmedS3Key(confirmData, s3KeyFromPresign),
      };
    },
    [API_BASE_URL],
  );

  const processImageFile = useCallback(
    async (file, inputEl) => {
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        toast.error("Please choose an image file.");
        if (inputEl) inputEl.value = "";
        return;
      }
      const maxBytes = 8 * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error("File is too large (max 8 MB).");
        if (inputEl) inputEl.value = "";
        return;
      }

      const email = String(profile?.email || "").trim();
      if (!email) {
        toast.error("Email not found on profile. Reload and try again.");
        if (inputEl) inputEl.value = "";
        return;
      }

      const previousImage = editProfileImage;
      clearLocalPreview();
      const localUrl = URL.createObjectURL(file);
      previewObjectUrlRef.current = localUrl;
      setImagePreview(localUrl);
      setEditProfileImage("");

      setUploadingImage(true);
      let didIncrement = false;
      try {
        loaderIncrement();
        didIncrement = true;
        const { imageUrl } = await uploadProfileImage(email, file);
        clearLocalPreview();
        setImagePreview(imageUrl);
        setEditProfileImage(imageUrl);
        toast.success("Profile image uploaded");
      } catch (error) {
        toast.error(error?.message || "Image upload failed");
        clearLocalPreview();
        setEditProfileImage(previousImage);
        setImagePreview(previousImage || "");
        if (inputEl) inputEl.value = "";
      } finally {
        if (didIncrement) loaderDecrement();
        setUploadingImage(false);
      }
    },
    [profile?.email, editProfileImage, clearLocalPreview, uploadProfileImage],
  );

  const onImageFileChange = (e) => {
    processImageFile(e.target.files?.[0], e.target);
  };

  const onDropImage = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (saving || uploadingImage) return;
    const file = e.dataTransfer?.files?.[0];
    processImageFile(file, imageInputRef.current);
  };

  const clearImage = () => {
    if (uploadingImage) return;
    clearLocalPreview();
    setEditProfileImage("");
    setImagePreview("");
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const openImagePicker = () => {
    if (!saving && !uploadingImage) imageInputRef.current?.click();
  };

  const handleSave = async () => {
    const name = editName.trim();
    const phone = editPhone.trim();
    const profileImage = editProfileImage.trim();

    if (!name) {
      toast.error("Name is required");
      return;
    }
    if (!phone) {
      toast.error("Phone number is required");
      return;
    }
    if (uploadingImage) {
      toast.error("Please wait for the image upload to finish.");
      return;
    }
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    setSaving(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "updateProfile",
        userId,
        name,
        phone,
        profileImage: profileImage || "",
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        toast.success(data?.message || "Profile updated successfully");
        const updated = pickProfile(data);
        const nextImage = getProfileImageUrl(updated) || profileImage || "";
        if (updated) {
          setProfile(updated);
          syncEditFromProfile(updated);
        } else {
          setProfile((prev) => ({
            ...(prev || {}),
            name,
            phone,
            profileImage: nextImage,
          }));
        }
        dispatch(
          updateUserProfile({
            name,
            profileImage: nextImage,
          }),
        );
        editingRef.current = false;
        setEditing(false);
        setLoading(true);
        await fetchProfile();
      } else {
        toast.error(data?.message || "Failed to update profile");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Something went wrong",
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setSaving(false);
    }
  };

  const name = profile?.name || profile?.fullName || "—";
  const email = profile?.email || "—";
  const phone = getProfilePhone(profile) || "—";
  const role = profile?.role || localStorage.getItem("role") || "—";
  const flatNumber = profile?.flatNumber;
  const block = profile?.blockNumber || profile?.block;
  const floor = profile?.floor;
  const squareFeet = profile?.squareFeet;
  const dateOfPropertyPurchase = profile?.dateOfPropertyPurchase;
  const ownerName = profile?.ownerName;
  const propertyType = profile?.propertyType;
  const occupancyStatus = profile?.occupancyStatus;
  const phoneNumber = profile?.phoneNumber;
  const whatsappNumber = profile?.whatsappNumber;
  const ownerPhoneNumber = profile?.ownerPhoneNumber;
  const ownerWhatsappNumber = profile?.ownerWhatsappNumber;
  const jointOwnerPhoneNumber = profile?.jointOwnerPhoneNumber;
  const jointOwnerWhatsappNumber = profile?.jointOwnerWhatsappNumber;

  const designation = profile?.designation;
  const executiveStatus = profile?.executiveStatus;
  const executiveLabel =
    EXECUTIVE_STATUS_LABELS[executiveStatus] || executiveStatus || "";
  const fromDate = profile?.fromDate;
  const toDate = profile?.toDate;

  const hasExecutive = !!(designation || executiveStatus);
  const ownerProperty = profile?.ownerProperty || null;
  const isTenantRole = String(role || "").toUpperCase() === "TENANT";
  const showOwnerProperty = !editing && isTenantRole && !!ownerProperty;

  const hasResidence = !!(
    block ||
    (floor != null && floor !== "") ||
    flatNumber ||
    (squareFeet != null && squareFeet !== "") ||
    occupancyStatus
  );
  const hasProperty = !!(ownerName || propertyType || dateOfPropertyPurchase);
  const hasContactNumbers = !!(
    phoneNumber ||
    whatsappNumber ||
    ownerPhoneNumber ||
    ownerWhatsappNumber ||
    jointOwnerPhoneNumber ||
    jointOwnerWhatsappNumber
  );

  const displayImage = editing ? imagePreview : getProfileImageUrl(profile);

  const initials =
    String(
      (editing ? editName : name) !== "—" ? (editing ? editName : name) : "U",
    )
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "U";

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Profile</h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              Your account details
            </p>
          </div>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {!editing ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={loading || !profile}
                  onClick={startEdit}
                >
                  Edit
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saving || uploadingImage}
                  onClick={handleSave}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={saving || uploadingImage}
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {loading && !profile ? (
            <div className="card">
              <div className="card-body text-muted">Loading profile…</div>
            </div>
          ) : profile ? (
            <div className="row">
              <div className="col-12 col-lg-4 mb-3">
                {editing ? (
                  <div className="community-create-photo-card h-100">
                    <div
                      className="community-create-photo-glow"
                      aria-hidden="true"
                    />
                    <div
                      className={`community-create-dropzone${dragOver ? " is-dragover" : ""}${
                        uploadingImage || saving ? " is-uploading" : ""
                      }${displayImage ? " has-image" : ""}`}
                      onClick={openImagePicker}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openImagePicker();
                        }
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                      }}
                      onDrop={onDropImage}
                      role="button"
                      tabIndex={0}
                      aria-label="Upload profile image"
                    >
                      <input
                        ref={imageInputRef}
                        type="file"
                        className="d-none"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={onImageFileChange}
                        disabled={saving || uploadingImage}
                      />

                      {displayImage ? (
                        <img
                          src={displayImage}
                          alt="Profile preview"
                          className="community-create-avatar-img"
                          onError={() => setImagePreview("")}
                        />
                      ) : (
                        <div className="community-create-avatar-fallback">
                          {initials}
                        </div>
                      )}

                      <div className="community-create-drop-hint">
                        {uploadingImage ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin" />{" "}
                            Uploading…
                          </>
                        ) : displayImage ? (
                          <>
                            <i className="fa-solid fa-camera" /> Click to change
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-cloud-arrow-up" /> Drop or
                            click
                          </>
                        )}
                      </div>
                    </div>

                    <p className="community-create-photo-note">
                      JPG / PNG / WebP · max 8 MB
                    </p>

                    <div className="d-flex flex-column" style={{ gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary btn-block"
                        onClick={openImagePicker}
                        disabled={saving || uploadingImage}
                      >
                        <i className="fa-solid fa-image mr-1" />{" "}
                        {uploadingImage
                          ? "Uploading…"
                          : displayImage
                            ? "Change photo"
                            : "Choose photo"}
                      </button>
                      {editProfileImage ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary btn-block"
                          onClick={clearImage}
                          disabled={saving || uploadingImage}
                        >
                          <i className="fa-solid fa-trash-can mr-1" /> Remove
                          photo
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="card" style={{ alignSelf: "flex-start" }}>
                    <div className="card-body text-center">
                      <div
                        className="d-inline-flex align-items-center justify-content-center mb-3"
                        style={{
                          width: 110,
                          height: 110,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(145deg, #0094a9, #007a8a)",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 28,
                          overflow: "hidden",
                        }}
                      >
                        {displayImage ? (
                          <img
                            src={displayImage}
                            alt={name}
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          initials
                        )}
                      </div>

                      <h2 className="h4 mb-1">{name}</h2>
                      <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                        {email}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-12 col-lg-8 mb-3">
                <div className="card h-100">
                  <div className="card-body">
                    <SectionTitle first>Account</SectionTitle>
                    {editing ? (
                      <div className="border rounded p-3 mb-0">
                        <div className="form-group">
                          <label className="form-label">
                            Name <span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">
                            Phone number{" "}
                            <span className="required-star">*</span>
                          </label>
                          <input
                            type="tel"
                            className="form-control"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label text-muted">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            value={email !== "—" ? email : ""}
                            readOnly
                            tabIndex={-1}
                            style={{
                              backgroundColor: "#f1f3f5",
                              color: "#868e96",
                              cursor: "not-allowed",
                              borderColor: "#e9ecef",
                            }}
                          />
                        </div>
                        <div className="form-group mb-0">
                          <label className="form-label text-muted">Role</label>
                          <input
                            type="text"
                            className="form-control"
                            value={role !== "—" ? role : ""}
                            readOnly
                            tabIndex={-1}
                            style={{
                              backgroundColor: "#f1f3f5",
                              color: "#868e96",
                              cursor: "not-allowed",
                              borderColor: "#e9ecef",
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="border rounded overflow-hidden mb-0">
                        <DetailRow label="Name" value={name} />
                        <DetailRow label="Email" value={email} />
                        <DetailRow label="Mobile" value={phone} />
                        <DetailRow label="Role" value={role} />
                        <DetailRow
                          label="Registered"
                          value={profile?.isVerified ? "Yes" : "No"}
                        />
                        <DetailRow
                          label="Status"
                          value={profile?.isActive ? "Active" : "Inactive"}
                        />
                      </div>
                    )}
                    {!editing && hasExecutive ? (
                      <>
                        <SectionTitle>Executive details</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          <DetailRow label="Position" value={designation} />
                          <DetailRow
                            label="Executive status"
                            value={executiveLabel}
                          />
                          <DetailRow
                            label="From date"
                            value={formatDMY(fromDate)}
                          />
                          <DetailRow
                            label="Date of Period Completion"
                            value={formatDMY(toDate)}
                          />
                        </div>
                      </>
                    ) : null}

                    {!editing && hasResidence ? (
                      <>
                        <SectionTitle>Residence</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          <DetailRow label="Block" value={block} />
                          <DetailRow label="Floor" value={floor} />
                          <DetailRow label="Flat no." value={flatNumber} />
                          <DetailRow
                            label="Square feet"
                            value={
                              squareFeet != null && squareFeet !== ""
                                ? String(squareFeet)
                                : ""
                            }
                          />
                          <DetailRow
                            label="Occupancy status"
                            value={occupancyStatus}
                          />
                        </div>
                      </>
                    ) : null}

                    {!editing && hasProperty ? (
                      <>
                        <SectionTitle>Property</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          <DetailRow label="Owner name" value={ownerName} />
                          <DetailRow
                            label="Property type"
                            value={propertyType}
                          />
                          <DetailRow
                            label="Date of property purchase"
                            value={formatDate(dateOfPropertyPurchase)}
                          />
                        </div>
                      </>
                    ) : null}

                    {!editing && hasContactNumbers ? (
                      <>
                        <SectionTitle>Contact numbers</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          <DetailRow label="Phone number" value={phoneNumber} />
                          <DetailRow
                            label="WhatsApp number"
                            value={whatsappNumber}
                          />
                          <DetailRow
                            label="Owner phone"
                            value={ownerPhoneNumber}
                          />
                          <DetailRow
                            label="Owner WhatsApp"
                            value={ownerWhatsappNumber}
                          />
                          <DetailRow
                            label="Joint owner phone"
                            value={jointOwnerPhoneNumber}
                          />
                          <DetailRow
                            label="Joint owner WhatsApp"
                            value={jointOwnerWhatsappNumber}
                          />
                        </div>
                      </>
                    ) : null}

                    {showOwnerProperty ? (
                      <>
                        <SectionTitle>Owner</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          <DetailRow label="Name" value={ownerProperty.name} />
                          <DetailRow
                            label="Email"
                            value={ownerProperty.email}
                          />
                          <DetailRow
                            label="Mobile"
                            value={ownerProperty.phone}
                          />
                        </div>

                        <SectionTitle>Property details</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          <DetailRow
                            label="Block"
                            value={ownerProperty.block}
                          />
                          <DetailRow
                            label="Flat no."
                            value={ownerProperty.flatNumber}
                          />
                          <DetailRow
                            label="BHK"
                            value={ownerProperty.bhkType}
                          />
                          <DetailRow
                            label="Sq.ft"
                            value={ownerProperty.squareFeet}
                          />
                        </div>

                        {ownerProperty.monthlyMaintenanceChargesBy ===
                        "By Tenant" ? (
                          <>
                            <SectionTitle>Maintenance</SectionTitle>
                            <div className="border rounded overflow-hidden mb-0">
                              <DetailRow
                                label="Monthly maintenance amount"
                                value={formatMoney(
                                  ownerProperty.monthlyMaintenanceAmount,
                                )}
                              />
                            </div>
                          </>
                        ) : null}

                        {ownerProperty.propertyType === "Joint Owner" ? (
                          <>
                            <SectionTitle>Joint owner</SectionTitle>
                            <div className="border rounded overflow-hidden mb-0">
                              <DetailRow
                                label="Joint owner name"
                                value={ownerProperty.jointOwnerName}
                              />
                              <DetailRow
                                label="Email"
                                value={ownerProperty.jointOwnerEmail}
                              />
                              <DetailRow
                                label="Mobile"
                                value={ownerProperty.jointOwnerMobile}
                              />
                              <DetailRow
                                label="WhatsApp"
                                value={ownerProperty.jointOwnerWhatsapp}
                              />
                            </div>
                          </>
                        ) : null}

                        {ownerProperty.propertyNature === "Let-out" ? (
                          <>
                            <SectionTitle>Let-out details</SectionTitle>
                            <div className="border rounded overflow-hidden mb-0">
                              <DetailRow
                                label="Let out portion"
                                value={ownerProperty.letOutPortion}
                              />
                              <DetailRow
                                label="Type of tenancy"
                                value={ownerProperty.typeOfTenancy}
                              />
                              <DetailRow
                                label="Lease period"
                                value={ownerProperty.leasePeriod}
                              />
                              <DetailRow
                                label="Tenancy starting date"
                                value={formatDate(
                                  ownerProperty.tenancyStartingDate,
                                )}
                              />
                              <DetailRow
                                label="Advance amount"
                                value={formatMoney(ownerProperty.advanceAmount)}
                              />
                              <DetailRow
                                label="Monthly rent"
                                value={formatMoney(ownerProperty.monthlyRent)}
                              />
                              <DetailRow
                                label="Monthly maintenance charges"
                                value={
                                  ownerProperty.monthlyMaintenanceChargesBy
                                }
                              />
                            </div>
                          </>
                        ) : null}
                      </>
                    ) : null}

                    {!editing &&
                    (profile?.advanceAmount != null ||
                      profile?.rentAmount != null ||
                      profile?.maintenanceAmount != null) ? (
                      <>
                        <SectionTitle>Charges</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          {profile?.advanceAmount != null ? (
                            <DetailRow
                              label="Advance"
                              value={formatMoney(profile.advanceAmount)}
                            />
                          ) : null}
                          {profile?.rentAmount != null ? (
                            <DetailRow
                              label="Rent"
                              value={formatMoney(profile.rentAmount)}
                            />
                          ) : null}
                          {profile?.maintenanceAmount != null ? (
                            <DetailRow
                              label="Maintenance"
                              value={formatMoney(profile.maintenanceAmount)}
                            />
                          ) : null}
                        </div>
                      </>
                    ) : null}

                    {!editing &&
                    (profile?.tenureStartDate || profile?.tenureEndDate) ? (
                      <>
                        <SectionTitle>Record</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          {profile?.tenureStartDate ? (
                            <DetailRow
                              label="Tenure start"
                              value={formatDateTime(profile.tenureStartDate)}
                            />
                          ) : null}
                          {profile?.tenureEndDate ? (
                            <DetailRow
                              label="Tenure end"
                              value={formatDateTime(profile.tenureEndDate)}
                            />
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-muted">
                Could not load profile. Try Refresh.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
